// src/hooks/useDesktopAuthBridge.js
// ============================================================================
// CRITCAST DESKTOP APP BRIDGE (desktop app v1; DESKTOP_APP_DESIGN_2026-10-20
// S2.2 web items 1-7, S1.8, S8.1)
//
// Inside the CritCast desktop app this site runs in the app's left view and is
// the only sign-in surface. This hook hands the app what it needs, through the
// site preload `window.critcashDesktop`:
//   - the signed-in member's Firebase ID token (auth.pushSession), on sign-in
//     and on every refresh (onIdTokenChanged), WITHHELD until the first
//     trusted users-doc verdict for that uid and while a Terms or onboarding
//     gate stands; the gate itself goes over auth.pushGate;
//   - null when the member signs out (never for a gate, never ahead of a user
//     switch: a different uid is pushed as itself and the app decides);
//   - the stream game catalog (stream.pushGameCatalog) right after each
//     accepted session push;
//   - answers to the app's token requests and sign-out requests.
// The same file exports subscribeDesktopNavigate (the app opens site pages in
// this view, S1.8) and the pure helpers that
// web-landing/scripts/test-desktop-bridge.mjs checks offline.
//
// Every path is a no-op outside the app (!isDesktopApp()), so critcash.com in
// a normal browser behaves exactly as before.
//
// No console call in this file: nothing here may ever print a token.
// ============================================================================

import { useEffect, useRef } from 'react';
import { onIdTokenChanged, getIdTokenResult, getIdToken, signOut } from 'firebase/auth';
import { getToken, onTokenChanged } from 'firebase/app-check';
import { doc, getDoc } from 'firebase/firestore';
import { auth, appCheck, db } from '../../firebaseConfig';
import { isDesktopApp } from '../utils/platform';
import { buildStreamGameCatalog } from '../constants/streamGames';

// No trusted users-doc snapshot within this long after the member arrived
// (offline with a cold cache): treat the gate as known, fail-open (S2.2 item 2).
export const GATE_VERDICT_TIMEOUT_MS = 10000;
// getToken(appCheck) must answer within this long or the push goes without it.
export const APP_CHECK_TIMEOUT_MS = 2000;
// The public profile name read is display only; it never holds a push longer.
export const PROFILE_NAME_TIMEOUT_MS = 2000;

// The app's validators (authBridge.validatePush and the catalog handler, S8.1).
// The web applies the same rules so the two sides cannot disagree.
export const USER_NAME_RE = /^[A-Za-z0-9_.-]{1,40}$/;
export const APP_CHECK_TOKEN_MAX = 4096;
export const GAME_KEY_RE = /^[a-z0-9_]{1,32}$/;
export const GAME_NAME_MAX = 60;
export const GAME_CATALOG_MAX = 200;
export const OTHER_GAME = Object.freeze({ key: 'other', name: 'Other' });
export const NAV_PATH_MAX = 2048;

// ============================================================================
// PURE HELPERS
// ============================================================================

/** The site bridge, or null outside the desktop app. */
export function getDesktopBridge() {
  if (!isDesktopApp()) return null;
  const bridge = window.critcashDesktop;
  return bridge && typeof bridge === 'object' ? bridge : null;
}

/**
 * true while the token must stay in the page: the users-doc verdict for this
 * uid is not in yet, or a Terms or onboarding gate stands (S2.2 item 3).
 */
export function shouldWithholdToken({ gateKnown, needsOnboarding, needsTosAccept } = {}) {
  return !gateKnown || !!needsOnboarding || !!needsTosAccept;
}

/** The pushGate reason for the current flags: 'onboarding', 'terms' or null. */
export function gateReasonFrom({ needsOnboarding, needsTosAccept } = {}) {
  if (needsOnboarding) return 'onboarding';
  if (needsTosAccept) return 'terms';
  return null;
}

/**
 * A users-doc snapshot is a trusted verdict when the doc exists, or when the
 * server (not a cold local cache) says it is missing: the rule App.js already
 * applies to "missing".
 */
export function isTrustedUsersSnapshot(snap) {
  if (!snap) return false;
  const exists = typeof snap.exists === 'function' ? !!snap.exists() : !!snap.exists;
  const fromCache = !!(snap.metadata && snap.metadata.fromCache);
  return exists || !fromCache;
}

/**
 * gateKnownUid, the uid whose users-doc verdict is in (App.js state).
 * Events:
 *   { type: 'user', uid }               auth moved to uid (null = signed out):
 *                                       a different uid resets it to null
 *   { type: 'snapshot', uid, trusted }  a users-doc snapshot; only a trusted
 *                                       one sets it (a cached "missing" does not)
 *   { type: 'error', uid }              the listener failed or threw (fail-open)
 *   { type: 'timeout', uid }            no trusted snapshot in time (fail-open)
 * currentUid (optional) is the uid auth holds now; an event for any other uid
 * changes nothing.
 */
export function nextGateKnownUid(prev, event, currentUid) {
  const before = typeof prev === 'string' && prev ? prev : null;
  if (!event || typeof event !== 'object') return before;
  const uid = typeof event.uid === 'string' && event.uid ? event.uid : null;
  if (event.type === 'user') return uid && uid === before ? before : null;
  if (!uid) return before;
  if (currentUid !== undefined && uid !== currentUid) return before;
  if (event.type === 'snapshot') return event.trusted ? uid : before;
  if (event.type === 'error' || event.type === 'timeout') return uid;
  return before;
}

/**
 * The auth.pushSession payload: { uid, idToken, expiresAtMs, userName?,
 * appCheckToken? } (S2.2, S8.1), or null when the inputs cannot make one.
 *   user            { uid, userName? }: the auth uid plus the PUBLIC profile
 *                   name (public_profiles/{uid}.userName), never displayName
 *   tokenResult     getIdTokenResult(user): { token, expirationTime }
 *   appCheckResult  { token } from getToken(appCheck), or null when it
 *                   rejected or took too long (appCheckWithin)
 * userName and appCheckToken are left out unless they pass the app's rules.
 */
export function sessionPayloadFrom(user, tokenResult, appCheckResult) {
  const uid = user && typeof user.uid === 'string' ? user.uid : '';
  if (!uid) return null;
  const idToken = tokenResult && typeof tokenResult.token === 'string' ? tokenResult.token : '';
  if (!idToken) return null;
  const expiresAtMs = Date.parse(tokenResult.expirationTime);
  if (!Number.isFinite(expiresAtMs)) return null;
  const payload = { uid, idToken, expiresAtMs };
  const name = user && typeof user.userName === 'string' ? user.userName.trim() : '';
  if (USER_NAME_RE.test(name)) payload.userName = name;
  const act = appCheckResult && typeof appCheckResult.token === 'string' ? appCheckResult.token : '';
  if (act.length >= 1 && act.length <= APP_CHECK_TOKEN_MAX) payload.appCheckToken = act;
  return payload;
}

/**
 * Resolves with fn()'s value, or with null when fn throws, rejects, or takes
 * longer than timeoutMs. Never rejects.
 */
export function settleWithin(fn, { timeoutMs, setTimeoutFn = setTimeout, clearTimeoutFn = clearTimeout } = {}) {
  if (typeof fn !== 'function') return Promise.resolve(null);
  return new Promise((resolve) => {
    let done = false;
    const finish = (value) => {
      if (done) return;
      done = true;
      try { clearTimeoutFn(timer); } catch (_) { /* fake timers */ }
      resolve(value);
    };
    const timer = setTimeoutFn(() => finish(null), timeoutMs);
    let pending;
    try { pending = Promise.resolve(fn()); } catch (_) { pending = Promise.resolve(null); }
    pending.then((value) => finish(value === undefined ? null : value), () => finish(null));
  });
}

/** { token } when getToken answers within the limit with a usable token, else null. */
export function appCheckWithin(getTokenFn, { timeoutMs = APP_CHECK_TIMEOUT_MS, setTimeoutFn, clearTimeoutFn } = {}) {
  return settleWithin(getTokenFn, { timeoutMs, setTimeoutFn, clearTimeoutFn }).then((result) => {
    if (!result || result.error || typeof result.token !== 'string' || !result.token) return null;
    return { token: result.token };
  });
}

/**
 * The stream.pushGameCatalog list: buildStreamGameCatalog() (featured keys
 * first, then the rest alphabetically) plus { key: 'other', name: 'Other' }
 * exactly once at the end. Entries the app would drop are left out here too.
 */
export function catalogPayload(build = buildStreamGameCatalog) {
  let list = [];
  try { list = build() || []; } catch (_) { list = []; }
  const out = [];
  const seen = new Set();
  for (const entry of Array.isArray(list) ? list : []) {
    if (out.length >= GAME_CATALOG_MAX - 1) break;
    const key = entry && typeof entry.key === 'string' ? entry.key : '';
    const name = entry && typeof entry.name === 'string' ? entry.name.trim() : '';
    if (!GAME_KEY_RE.test(key) || key === OTHER_GAME.key || seen.has(key)) continue;
    if (!name || name.length > GAME_NAME_MAX) continue;
    seen.add(key);
    out.push({ key, name });
  }
  out.push({ key: OTHER_GAME.key, name: OTHER_GAME.name });
  return out;
}

/** The focused leaf of a navigation state: { name, params } or null. */
export function deepestRoute(state) {
  let current = state;
  let leaf = null;
  for (let depth = 0; depth < 16; depth += 1) {
    if (!current || !Array.isArray(current.routes) || current.routes.length === 0) break;
    const { routes } = current;
    const index = Number.isInteger(current.index) && current.index >= 0 && current.index < routes.length
      ? current.index
      : routes.length - 1;
    const route = routes[index];
    if (!route || typeof route.name !== 'string' || !route.name) break;
    leaf = route;
    current = route.state;
  }
  return leaf ? { name: leaf.name, params: leaf.params } : null;
}

/**
 * The route a site path such as '/streaming' or '/chat/<userName>' opens, read
 * through React Navigation's getStateFromPath with App.js's linking config.
 * null for a path no screen matches (the app then loads the URL instead).
 */
export function pathToRoute(path, { config, getStateFromPath } = {}) {
  if (typeof path !== 'string' || !path || path.length > NAV_PATH_MAX || path.charAt(0) !== '/') return null;
  if (typeof getStateFromPath !== 'function') return null;
  let state;
  try { state = getStateFromPath(path.replace(/^\//, ''), config); } catch (_) { return null; }
  return state ? deepestRoute(state) : null;
}

function callMethod(target, name, ...args) {
  const fn = target && typeof target[name] === 'function' ? target[name] : null;
  if (!fn) return Promise.resolve(undefined);
  try {
    return Promise.resolve(fn(...args)).catch(() => undefined);
  } catch (_) {
    return Promise.resolve(undefined);
  }
}

function subscribe(target, name, cb) {
  const fn = target && typeof target[name] === 'function' ? target[name] : null;
  if (!fn) return null;
  try {
    const off = fn(cb);
    return typeof off === 'function' ? off : null;
  } catch (_) {
    return null;
  }
}

// ============================================================================
// NAVIGATE CONSUMER (S1.8 step 1)
// ============================================================================

/**
 * The app sends `navigate` with a site path (Broadcast panel links, tray,
 * deep links). Resolve it in-app and ack, so the app skips its full-page
 * load fallback (it loads CRITCASH_URL + path when no ack comes in 1500 ms).
 * An unknown path or a navigator that is not ready yet gets no ack. Returns
 * the unsubscribe function (a no-op outside the app).
 */
export function subscribeDesktopNavigate({ navigationRef, config, getStateFromPath, bridge } = {}) {
  const target = bridge === undefined ? getDesktopBridge() : bridge;
  const nav = target && target.navigation;
  if (!nav) return () => {};
  const off = subscribe(nav, 'onNavigate', (path) => {
    const leaf = pathToRoute(path, { config, getStateFromPath });
    if (!leaf) return;
    if (!navigationRef || typeof navigationRef.isReady !== 'function' || !navigationRef.isReady()) return;
    try {
      navigationRef.navigate(leaf.name, leaf.params);
    } catch (_) {
      return;
    }
    callMethod(nav, 'ack', path);
  });
  return off || (() => {});
}

// ============================================================================
// SESSION BRIDGE CONTROLLER (S2.2 web items 1-7)
// ============================================================================

/**
 * The bridge logic with every dependency injected (the hook below wires the
 * real ones; the harness wires fakes).
 *   bridge             window.critcashDesktop
 *   getCurrentUser     () => auth.currentUser
 *   getIdTokenResult   (user) => Promise<{ token, expirationTime }> (no force)
 *   forceRefresh       (user) => Promise (getIdToken(user, true))
 *   getAppCheckToken   () => Promise<{ token }>, or null when App Check is off
 *   getUserName        (uid) => Promise<string|null> (public profile name)
 *   signOut            () => Promise (signOut(auth))
 *   resetToLogin       () => void (navigation reset to Login)
 * Methods: start(), setGate({ uid, gateKnownUid, needsOnboarding,
 * needsTosAccept }), onIdToken(user|null), onAppCheckToken(),
 * onTokenRequested(payload), onSignOutRequested(payload), dispose().
 */
export function createDesktopAuthBridge(deps = {}) {
  const {
    bridge,
    getCurrentUser = () => null,
    getIdTokenResult: readTokenResult = () => Promise.resolve(null),
    forceRefresh = () => Promise.resolve(),
    getAppCheckToken = null,
    getUserName = null,
    signOut: doSignOut = () => Promise.resolve(),
    resetToLogin = () => {},
    catalog = catalogPayload,
    setTimeoutFn = setTimeout,
    clearTimeoutFn = clearTimeout,
    gateTimeoutMs = GATE_VERDICT_TIMEOUT_MS,
    appCheckTimeoutMs = APP_CHECK_TIMEOUT_MS,
    profileTimeoutMs = PROFILE_NAME_TIMEOUT_MS,
  } = deps;
  const authApi = (bridge && bridge.auth) || null;
  const streamApi = (bridge && bridge.stream) || null;

  let started = false;
  let disposed = false;
  let gate = { uid: null, gateKnownUid: null, needsOnboarding: false, needsTosAccept: false };
  let timedOutUid = null;
  let gateTimer = null;
  let lastGate = null;    // { uid, reason } last sent through pushGate for this uid
  let lastPushed = null;  // what the last session push carried, to skip exact repeats
  let pushSeq = 0;
  let forcePending = false;
  // uid -> { read, good, stale }: the public profile name. Read once per uid,
  // read again after a token refresh or a gate clear (so a rename shows up
  // without a reload), and dropped on sign-out or a user switch.
  const names = new Map();
  const offs = [];

  const currentUser = () => {
    try { return getCurrentUser() || null; } catch (_) { return null; }
  };
  const isKnown = (uid) => !!uid && (gate.gateKnownUid === uid || timedOutUid === uid);
  const allowed = (uid) => !!uid && gate.uid === uid && !shouldWithholdToken({
    gateKnown: isKnown(uid),
    needsOnboarding: gate.needsOnboarding,
    needsTosAccept: gate.needsTosAccept,
  });

  function clearGateTimer() {
    if (gateTimer !== null) {
      try { clearTimeoutFn(gateTimer); } catch (_) { /* fake timers */ }
      gateTimer = null;
    }
  }

  function userNameFor(uid) {
    if (typeof getUserName !== 'function') return Promise.resolve(null);
    const prior = names.get(uid);
    if (prior && !prior.stale) return prior.read;
    const entry = { read: null, good: prior ? prior.good : null, stale: false };
    entry.read = settleWithin(() => getUserName(uid), { timeoutMs: profileTimeoutMs, setTimeoutFn, clearTimeoutFn })
      .then((name) => {
        if (typeof name === 'string' && name) {
          entry.good = name;
          return name;
        }
        // A failed or empty read is tried again on the next push; the last
        // good name for this uid stands in meanwhile.
        entry.stale = true;
        if (!entry.good && prior && prior.good) entry.good = prior.good;
        return entry.good;
      });
    names.set(uid, entry);
    return entry.read;
  }

  // The next push reads the name again (the last good one stands in if that read fails).
  function staleName(uid) {
    const entry = uid ? names.get(uid) : null;
    if (entry) entry.stale = true;
  }

  function pushCatalog() {
    let list;
    try { list = catalog(); } catch (_) { return; }
    callMethod(streamApi, 'pushGameCatalog', list);
  }

  function sendGate(uid, reason) {
    lastGate = { uid, reason };
    callMethod(authApi, 'pushGate', { uid, reason });
  }

  // Push the current member's session when the gate allows it. force = send
  // even when it repeats the last push (the app dropped its copy, or asked).
  async function pushCurrent(force) {
    if (disposed || !authApi) return false;
    const user = currentUser();
    if (!user || !allowed(user.uid)) return false;
    const { uid } = user;
    const seq = ++pushSeq;
    if (force) forcePending = true;
    let tokenResult = null;
    try { tokenResult = await readTokenResult(user); } catch (_) { tokenResult = null; }
    const [appCheckResult, userName] = await Promise.all([
      appCheckWithin(getAppCheckToken, { timeoutMs: appCheckTimeoutMs, setTimeoutFn, clearTimeoutFn }),
      userNameFor(uid),
    ]);
    // A newer push started meanwhile: it carries the force flag on.
    if (disposed || seq !== pushSeq) return false;
    const now = currentUser();
    if (!now || now.uid !== uid || !allowed(uid)) return false;
    const payload = sessionPayloadFrom({ uid, userName }, tokenResult, appCheckResult);
    if (!payload) return false;
    const mustSend = forcePending;
    forcePending = false;
    const appCheckToken = payload.appCheckToken || null;
    if (!mustSend && lastPushed && lastPushed.uid === uid
      && lastPushed.idToken === payload.idToken && lastPushed.appCheckToken === appCheckToken) {
      return false;
    }
    lastPushed = { uid, idToken: payload.idToken, appCheckToken };
    const result = await callMethod(authApi, 'pushSession', payload);
    if (result && result.accepted === true) {
      pushCatalog();
    } else if (lastPushed && lastPushed.idToken === payload.idToken) {
      lastPushed = null;   // not taken: the next push goes out again
    }
    return true;
  }

  // The gate verdict for the current uid changed (or just arrived).
  function evaluate() {
    const { uid } = gate;
    if (disposed || !uid || !isKnown(uid)) return;
    const reason = gateReasonFrom(gate);
    if (lastGate && lastGate.uid === uid && lastGate.reason === reason) return;
    sendGate(uid, reason);
    if (reason === null) {
      // First verdict with no gate, or a gate just cleared (accepted Terms and
      // finished onboarding never fire onIdTokenChanged): push once, with the
      // name read again (onboarding may have just set or changed it).
      staleName(uid);
      pushCurrent(true);
    } else {
      // The app drops the token while gated; the clear re-pushes it.
      lastPushed = null;
    }
  }

  function setGate(next = {}) {
    if (disposed) return;
    const uid = typeof next.uid === 'string' && next.uid ? next.uid : null;
    if (uid !== gate.uid) {
      clearGateTimer();
      timedOutUid = null;
      lastGate = null;
      lastPushed = null;
      names.clear();
    }
    gate = {
      uid,
      gateKnownUid: typeof next.gateKnownUid === 'string' && next.gateKnownUid ? next.gateKnownUid : null,
      needsOnboarding: !!next.needsOnboarding,
      needsTosAccept: !!next.needsTosAccept,
    };
    if (!uid || isKnown(uid)) {
      clearGateTimer();
    } else if (gateTimer === null) {
      gateTimer = setTimeoutFn(() => {
        gateTimer = null;
        if (disposed || gate.uid !== uid) return;
        timedOutUid = nextGateKnownUid(timedOutUid, { type: 'timeout', uid }, gate.uid);
        evaluate();
      }, gateTimeoutMs);
    }
    evaluate();
  }

  // onIdTokenChanged: sign-in, sign-out and every token refresh.
  function onIdToken(user) {
    if (disposed || !authApi) return;
    if (!user) {
      lastPushed = null;
      names.clear();
      callMethod(authApi, 'pushSession', null);
      return;
    }
    // A refreshed token: read the name again with it (a rename mid-session).
    staleName(user.uid);
    pushCurrent(false);
  }

  // App Check minted a new token: re-push when the session is pushable.
  function onAppCheckToken() {
    if (disposed) return;
    pushCurrent(false);
  }

  function onTokenRequested(payload) {
    if (disposed || !authApi) return;
    const reason = typeof payload === 'string' ? payload : (payload && payload.reason);
    const user = currentUser();
    if (reason === 'expiring' || reason === 'rejected') {
      if (!user) return;
      // A forced refresh fires onIdTokenChanged; the push after it is a
      // repeat of that one and is skipped, unless the listener never fired.
      Promise.resolve()
        .then(() => forceRefresh(user))
        .catch(() => undefined)
        .then(() => pushCurrent(false));
      return;
    }
    if (reason === 'startup') {
      pushCurrent(true);
      return;
    }
    if (reason === 'authStateCheck') {
      if (!user) {
        lastPushed = null;
        names.clear();
        callMethod(authApi, 'pushSession', null);
        return;
      }
      if (allowed(user.uid)) {
        pushCurrent(true);
        return;
      }
      if (gate.uid === user.uid && isKnown(user.uid)) {
        sendGate(user.uid, gateReasonFrom(gate));
      }
      // Verdict not in yet: it answers on its own (pushGate, then the push).
    }
  }

  async function onSignOutRequested() {
    if (disposed) return;
    // onIdTokenChanged(null) then pushes null to the app.
    try { await doSignOut(); } catch (_) { /* the app's own fallback covers a failed sign-out */ }
    if (disposed) return;
    // Land on Login, so the next streamer on a shared PC sees the sign-in form.
    try { resetToLogin(); } catch (_) { /* navigator not ready */ }
  }

  function start() {
    if (started || disposed || !authApi) return;
    started = true;
    // Listen first: the app replays a pending request as soon as ready() lands.
    const offSignOut = subscribe(authApi, 'onSignOutRequested', (payload) => { onSignOutRequested(payload); });
    const offToken = subscribe(authApi, 'onTokenRequested', (payload) => { onTokenRequested(payload); });
    if (offSignOut) offs.push(offSignOut);
    if (offToken) offs.push(offToken);
    callMethod(authApi, 'ready');
  }

  function dispose() {
    if (disposed) return;
    disposed = true;
    clearGateTimer();
    while (offs.length) {
      const off = offs.pop();
      try { off(); } catch (_) { /* already gone */ }
    }
  }

  return { start, setGate, onIdToken, onAppCheckToken, onTokenRequested, onSignOutRequested, dispose };
}

// ============================================================================
// THE HOOK (mounted once in App.js beside the auth effect)
// ============================================================================

function liveDeps(navigationRef) {
  return {
    getCurrentUser: () => auth.currentUser,
    getIdTokenResult: (user) => getIdTokenResult(user),
    forceRefresh: (user) => getIdToken(user, true),
    getAppCheckToken: appCheck ? () => getToken(appCheck) : null,
    getUserName: (uid) => getDoc(doc(db, 'public_profiles', uid))
      .then((snap) => (snap.exists() ? (snap.data() || {}).userName : null)),
    signOut: () => signOut(auth),
    resetToLogin: () => {
      if (navigationRef && typeof navigationRef.isReady === 'function' && navigationRef.isReady()) {
        navigationRef.reset({ index: 0, routes: [{ name: 'Login' }] });
      }
    },
  };
}

/**
 * useDesktopAuthBridge({ user, gateKnownUid, needsOnboarding, needsTosAccept,
 * navigationRef }): user is App.js's onAuthStateChanged user; gateKnownUid,
 * needsOnboarding and needsTosAccept come from its users-doc listener.
 */
export default function useDesktopAuthBridge({
  user,
  gateKnownUid,
  needsOnboarding,
  needsTosAccept,
  navigationRef,
} = {}) {
  const controllerRef = useRef(null);

  useEffect(() => {
    const bridge = getDesktopBridge();
    if (!bridge || !bridge.auth) return undefined;
    const controller = createDesktopAuthBridge({ bridge, ...liveDeps(navigationRef) });
    controllerRef.current = controller;
    controller.start();
    const offs = [];
    try {
      const off = onIdTokenChanged(auth, (u) => controller.onIdToken(u));
      if (typeof off === 'function') offs.push(off);
    } catch (_) { /* auth not available */ }
    if (appCheck) {
      try {
        const off = onTokenChanged(appCheck, () => controller.onAppCheckToken(), () => {});
        if (typeof off === 'function') offs.push(off);
      } catch (_) { /* App Check not available in this view */ }
    }
    return () => {
      while (offs.length) {
        const off = offs.pop();
        try { off(); } catch (_) { /* already gone */ }
      }
      controller.dispose();
      if (controllerRef.current === controller) controllerRef.current = null;
    };
  }, []);

  const uid = (user && user.uid) || null;
  useEffect(() => {
    if (controllerRef.current) {
      controllerRef.current.setGate({ uid, gateKnownUid, needsOnboarding, needsTosAccept });
    }
  }, [uid, gateKnownUid, needsOnboarding, needsTosAccept]);
}
